import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import EditablePriceItem from "../Package/EditablePriceItem";
import { toast } from "react-toastify";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Card, Button, Row, Col, Spinner, ListGroup, Table } from "react-bootstrap";
import moment from "moment";
import { FaPencilAlt, FaWhatsapp } from 'react-icons/fa';
// import PackageShareModal from "../Package/PackageShareModal"; // Reuse this if share functionality is same

// --- Helper to safely parse JSON strings from Holiday object ---
const safeParse = (data) => {
    if (!data) return [];
    try {
        return typeof data === 'string' ? JSON.parse(data) : data;
    } catch (e) {
        console.error("Parse error", e);
        return [];
    }
};

const FullPageDisplayLayout = ({ holidayData, onEdit, onPriceSave }) => {
  if (!holidayData) return <p className="text-center">No holiday data available.</p>;

  // Parsing JSON fields from the Holiday Object
  const itineraries = safeParse(holidayData.holidayItinerary);
  const flightBookings = safeParse(holidayData.holidayFlight);
  const hotelBookings = safeParse(holidayData.holidayHotel);
  const sightSeeings = safeParse(holidayData.holidaySightSeeing);
  const transfers = safeParse(holidayData.holidayTransfer);
  const inclusions = safeParse(holidayData.holidayInclusions || holidayData.holidayInclude);
  const exclusions = safeParse(holidayData.holidayExclusions);
  const terms = safeParse(holidayData.holidayTerms);

  const EditButton = ({ tabIndex }) => (
    <Button variant="primary" size="sm" onClick={() => onEdit(tabIndex)}>
      <FaPencilAlt size={12} className="me-1" /> Edit
    </Button>
  );

  return (
    <div>
      <Row className="mb-4 g-4">
        <Col md={7}>
          <img
            src={holidayData.imageSrc || 'https://via.placeholder.com/800x500?text=Holiday+Image'}
            alt={holidayData.holidayName}
            className="img-fluid rounded shadow-sm"
            style={{ width: "100%", height: "350px", objectFit: "cover" }}
          />
        </Col>
        <Col md={5}>
          <Card className="h-100 shadow-sm">
            <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
              Holiday Summary
              <EditButton tabIndex={0} /> 
            </Card.Header>
             <div className="list-group list-group-flush">
                <div className="list-group-item"><strong>Duration:</strong> {holidayData.duration}</div>
                <div className="list-group-item"><strong>Start Date:</strong> {moment(holidayData.startedDate).format("MMMM Do, YYYY")}</div>
                <div className="list-group-item"><strong>Destinations:</strong> {holidayData.destinationsCover}</div>

                <EditablePriceItem 
                    label="Flight Price" 
                    value={holidayData.flightPrice} 
                    currency={holidayData.currency || "INR"} 
                    fieldKey="flightPrice" 
                    onSave={onPriceSave} 
                />
                
                <EditablePriceItem 
                    label="Hotel Price" 
                    value={holidayData.hotelPrice} 
                    currency={holidayData.currency || "INR"} 
                    fieldKey="hotelPrice" 
                    onSave={onPriceSave}
                />
                
                <EditablePriceItem label="Sightseeing Price" value={holidayData.sightSeeingPrice} currency={holidayData.currency || "INR"} fieldKey="sightSeeingPrice" onSave={onPriceSave} />
                <EditablePriceItem label="Transfer Price" value={holidayData.transferPrice} currency={holidayData.currency || "INR"} fieldKey="transferPrice" onSave={onPriceSave} />
                <EditablePriceItem label="Other Price" value={holidayData.otherPrice} currency={holidayData.currency || "INR"} fieldKey="otherPrice" onSave={onPriceSave} />
                <EditablePriceItem label="Purchase Price" value={holidayData.purchasePrice} currency={holidayData.currency || "INR"} fieldKey="purchasePrice" onSave={onPriceSave} />

                <EditablePriceItem 
                  label="Sale Price" 
                  value={holidayData.salePrice} // Note: Holiday uses salePrice, Package used price
                  currency={holidayData.currency || "INR"} 
                  fieldKey="salePrice" 
                  onSave={onPriceSave} 
                />
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4 shadow-sm">
        <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
          Overview
          <EditButton tabIndex={0} /> 
        </Card.Header>
        <Card.Body>
          <p className="mb-0">{holidayData.description}</p>
        </Card.Body>
      </Card>

      {/* --- Itinerary Section --- */}
      {itineraries.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Itinerary
            <EditButton tabIndex={1} /> 
          </Card.Header>
          <Card.Body>
            {itineraries
              .sort((a, b) => new Date(a.Date || a.date) - new Date(b.Date || b.date))
              .map((item, index) => (
                <div key={index} className="p-3 mb-3 border rounded bg-white">
                  <h6 className="fw-bold">{moment(item.Date || item.date).format("MMMM Do, YYYY")} - {item.Title || item.title}</h6>
                  <p className="mb-1"><strong>Location:</strong> {item.Location || item.location}</p>
                  <p className="mb-1 text-muted">{item.Description || item.description}</p>
                </div>
              ))}
          </Card.Body>
        </Card>
      )}

      {flightBookings.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Flight Details
            <EditButton tabIndex={2} /> 
          </Card.Header>
          <Card.Body>
            {flightBookings.map((flight, index) => (
              <div key={index} className="p-3 mb-3 border rounded bg-white">
                <h6 className="fw-bold">{flight.Airline || flight.airline} - {flight.FlightNumber || flight.flightNumber} ({flight.CabinClass || flight.cabinClass})</h6>
                <Row>
                  <Col md={6}><strong>From:</strong> {flight.DepartureCity || flight.departureCity}</Col>
                  <Col md={6}><strong>To:</strong> {flight.ArrivalCity || flight.arrivalCity}</Col>
                </Row>
                <Row>
                  <Col md={6}><small><strong>Departure:</strong> {moment(flight.DepartureDateTime || flight.departureDateTime).format("MMM Do, YYYY, h:mm a")}</small></Col>
                  <Col md={6}><small><strong>Arrival:</strong> {moment(flight.ArrivalDateTime || flight.arrivalDateTime).format("MMM Do, YYYY, h:mm a")}</small></Col>
                </Row>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {hotelBookings.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Hotel Bookings
            <EditButton tabIndex={3} /> 
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr><th>Hotel</th><th>City</th><th>Check-In</th><th>Check-Out</th><th>Room Type</th></tr>
              </thead>
              <tbody>
                {hotelBookings.map((hotel, index) => (
                  <tr key={index}>
                    <td>{hotel.HotelName || hotel.hotelName}</td>
                    <td>{hotel.City || hotel.city}</td>
                    <td>{moment(hotel.CheckInDate || hotel.checkInDate).format("MMM Do, YYYY")}</td>
                    <td>{moment(hotel.CheckOutDate || hotel.checkOutDate).format("MMM Do, YYYY")}</td>
                    <td>{hotel.RoomType || hotel.roomType}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {sightSeeings.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Sightseeing
            <EditButton tabIndex={4} /> 
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr><th>Date</th><th>Destination</th><th>Attraction</th></tr>
              </thead>
              <tbody>
                {sightSeeings.map((sight, index) => (
                  <tr key={index}>
                    <td>{moment(sight.Date || sight.date).format("MMM Do, YYYY")}</td>
                    <td>{sight.Destination || sight.destination}</td>
                    <td>{sight.SightseeingName || sight.sightseeingName}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      
      {transfers.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Transfers
            <EditButton tabIndex={5} />
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr><th>Date</th><th>From</th><th>To</th><th>Vehicle</th></tr>
              </thead>
              <tbody>
                {transfers.map((transfer, index) => (
                  <tr key={index}>
                    <td>{moment(transfer.Date || transfer.date).format("MMM Do, YYYY")}</td>
                    <td>{transfer.FromLocation || transfer.fromLocation}</td>
                    <td>{transfer.ToLocation || transfer.toLocation}</td>
                    <td>{transfer.VehicleDetails || transfer.vehicleDetails}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Row className="mb-4">
        {inclusions.length > 0 && (
          <Col md={6}>
            <Card className="shadow-sm h-100">
              <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
                What's Included
                <EditButton tabIndex={6} /> 
              </Card.Header>
              <ListGroup variant="flush">
                {inclusions.map((item, index) => (
                  <ListGroup.Item key={index}>{item.Description || item.description}</ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          </Col>
        )}
        {exclusions.length > 0 && (
          <Col md={6}>
            <Card className="shadow-sm h-100">
              <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
                What's Excluded
                <EditButton tabIndex={7} /> 
              </Card.Header>
              <ListGroup variant="flush">
                {exclusions.map((item, index) => (
                  <ListGroup.Item key={index}>{item.Description || item.description}</ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          </Col>
        )}
      </Row>

      {terms.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
            Terms & Conditions
            <EditButton tabIndex={8} /> 
          </Card.Header>
          <Card.Body>
            {terms.map((term, index) => (
              <div key={index} className="mb-3">
                <h6 className="fw-bold">{term.Title || term.title}</h6>
                <p className="text-muted mb-0">{term.Description || term.description}</p>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

const HolidayDetailsView = ({ packageId, packageDetails, onEditRequest }) => { 
  const { user } = useAuth();
  const [holidayData, setHolidayData] = useState(packageDetails);
  const [isDataLoading, setIsDataLoading] = useState(!packageDetails);

  // Sync prop update
  useEffect(() => {
    if(packageDetails) {
        setHolidayData(packageDetails);
        setIsDataLoading(false);
    }
  }, [packageDetails]);

  const handlePriceUpdate = async (fieldKey, newValue) => {
    if (!holidayData) return;

    // Create a copy and update specific price
    const updatedPkg = { ...holidayData };
    updatedPkg[fieldKey] = parseFloat(newValue) || 0; 

    // Recalculate Purchase Price (Sum of base costs)
    const newPurchasePrice = 
        (updatedPkg.flightPrice || 0) + 
        (updatedPkg.hotelPrice || 0) + 
        (updatedPkg.sightSeeingPrice || 0) + 
        (updatedPkg.transferPrice || 0) + 
        (updatedPkg.otherPrice || 0);
    
    // Note: If you update Sale Price (fieldKey='salePrice'), purchasePrice remains sum of costs
    if(fieldKey !== 'salePrice' && fieldKey !== 'purchasePrice') {
         updatedPkg.purchasePrice = newPurchasePrice;
    }

    try {
        const formData = new FormData();
        // Append all fields from current object
        Object.entries(updatedPkg).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                 // For JSON strings, append as is. For others, append string value
                 formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
            }
        });
        
        formData.append("holidayId", packageId);
        if(user?.id) formData.append("userId", user.id);

        await apiService.update(config.UPDATEHOLIDAY, formData);

        // Update local state
        setHolidayData(updatedPkg);
        toast.success("Price updated successfully");

    } catch (error) {
        toast.error("Failed to update price.");
        console.error(error);
    }
  };

  const handleShareClick = () => {
    if (user && user.whatsAppNumber) {
      toast.info("Sharing feature coming soon!"); 
      // setShowShareModal(true); // Enable when ShareModal is ready
    } else {
      toast.warn("Please add your WhatsApp number in your profile.");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 ms-3">
          {isDataLoading ? "Loading..." : holidayData?.holidayName}
        </h4>
         {!isDataLoading && holidayData && (
            <Button variant="success" onClick={handleShareClick} className="me-3" >
                <FaWhatsapp className="me-2" /> Share
            </Button>
        )}
      </div>
      
     {isDataLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading details...</p>
        </div>
      ) : holidayData ? (
        <FullPageDisplayLayout 
            holidayData={holidayData} 
            onEdit={onEditRequest} 
            onPriceSave={handlePriceUpdate} 
        />
      ) : (
        <div className="text-center p-5"><p>Could not load details.</p></div>
      )}
    </div>
  );
};

export default HolidayDetailsView;